/**
 * M4S-5777 X7 — a crosstab's study type is known, not guessed.
 *
 * Until X7 the workspace asked `isIoohStudy(questions, name)` on every render, so renaming a
 * tracker's crosstab "… Brand Lift" turned it into an iOOH crosstab mid-session (the base, the
 * nets, the significance). Now the type is decided ONCE and saved with the crosstab:
 *
 *  - at create, chosen in the New crosstab dialog (`?type=`), else today's guess
 *    (`suggestStudyType`) taken from the questions and the name it was created with;
 *  - for a crosstab saved before X7, the same guess taken once, when the record is opened, with the
 *    record's own name, and frozen for the session; its first edit saves it (as X14's ST3 stamps
 *    statistics), so from then on it never moves.
 *
 * `ai_layout.state.studyType` (`StudyTypeRecord`) is record state, not a setting: it holds the
 * type, how it was decided, the setup the crosstab started from (X8) and the statistics standard
 * it recorded (X67). A project-level type is out of scope (decision T1: no project table we own).
 *
 * The guard (`study-type.guard.test.ts`) keeps `isIoohStudy` to this file: everything else asks the
 * resolved type.
 *
 * Pure: no React, no storage, no network.
 */

import { isIoohStudy } from './iooh-study';
import { parseStandardRef, type StandardRef } from './stats-standard';

/** The study types the PRD names (X6), in the order the New crosstab dialog lists them. */
export const STUDY_TYPES = [
    { key: 'iooh', label: 'iOOH Brand Lift' },
    { key: 'tracker', label: 'Tracker or waves' },
    { key: 'ad-hoc', label: 'Ad hoc or custom' },
    { key: 'store-intercept', label: 'Store, intercept or directed shop' },
    { key: 'diary', label: 'Diary' },
    { key: 'concept-test', label: 'Concept or creative test' },
    { key: 'monthly-opinions', label: 'Monthly Opinions' },
] as const;

export type StudyTypeKey = (typeof STUDY_TYPES)[number]['key'];

const KEYS: ReadonlySet<string> = new Set(STUDY_TYPES.map((t) => t.key));

export function isStudyTypeKey(value: unknown): value is StudyTypeKey {
    return typeof value === 'string' && KEYS.has(value);
}

export function studyTypeLabel(key: StudyTypeKey): string {
    return STUDY_TYPES.find((t) => t.key === key)?.label ?? key;
}

/**
 * Today's guess, kept exactly: iOOH when the catalog has two or more iOOH section questions or the
 * name says so (`isIoohStudy`), otherwise ad hoc. It is a SUGGESTION: the dialog shows it as the
 * default, and it is read once per crosstab, never again after the type is saved.
 */
export function suggestStudyType(
    questions: readonly { qid: string }[],
    name: string,
): StudyTypeKey {
    return isIoohStudy(questions, name) ? 'iooh' : 'ad-hoc';
}

/** How a crosstab started (X9). Recorded so a reopen can respect it (a blank iOOH stays blank). */
export type CrosstabStart = 'setup' | 'blank' | 'copy' | 'dani';

const STARTS: readonly CrosstabStart[] = ['setup', 'blank', 'copy', 'dani'];

export function isCrosstabStart(value: unknown): value is CrosstabStart {
    return STARTS.includes(value as CrosstabStart);
}

/**
 * How a create starts (X9), from `?start=` and whether a DANI plan (`?dani=`, X150) is waiting.
 * A plan from a DANI chat names its own rows and banner, so on a non-iOOH study the crosstab starts
 * blank: what waits for Run is the plan alone, not the plan over a setup nobody asked for. An iOOH
 * study keeps its setup under the plan, so its standard nets and Top 2 picks stay (decision L3).
 * A copy is never a start here; it has its own intent.
 */
export function startForCreate(
    startParam: unknown,
    { daniPlan, iooh }: { daniPlan: boolean; iooh: boolean },
): CrosstabStart {
    if (isCrosstabStart(startParam) && startParam !== 'copy') return startParam;
    if (daniPlan && !iooh) return 'blank';
    return 'setup';
}

/** Which setup a crosstab started from, frozen at create (X8). */
export interface SetupRef {
    studyType: StudyTypeKey;
    version: number;
    /** `admin`: a row of `pdlc_crosstab_setup`; `code`: the fallback in `study-setup.ts`. */
    origin: 'admin' | 'code';
}

/** `ai_layout.state.studyType`. */
export interface StudyTypeRecord {
    v: 1;
    type: StudyTypeKey;
    /** `chosen` in the New crosstab dialog, or `suggested` by today's guess. */
    source: 'chosen' | 'suggested';
    /** How the crosstab started, when it was made with X9; absent on older records. */
    start?: CrosstabStart;
    /** The setup it started from; null for a crosstab made before setups (or a copy of one). */
    setup: SetupRef | null;
    /** The statistics standard it recorded (X67); null for a crosstab made before standards. */
    standard: StandardRef | null;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v);

function parseSetupRef(raw: unknown): SetupRef | null {
    if (!isObject(raw) || !isStudyTypeKey(raw.studyType)) return null;
    const version = raw.version;
    if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) return null;
    if (raw.origin !== 'admin' && raw.origin !== 'code') return null;
    return { studyType: raw.studyType, version, origin: raw.origin };
}

/**
 * The record's block in its one canonical shape (fixed key order, so the JSONB round trip signs
 * alike), or null when there is none or its type is unknown. Parsed on the save route's write and
 * on every read, like every other record block.
 */
export function parseStudyTypeRecord(raw: unknown): StudyTypeRecord | null {
    if (!isObject(raw) || raw.v !== 1 || !isStudyTypeKey(raw.type)) return null;
    const source = raw.source === 'chosen' ? 'chosen' : 'suggested';
    return {
        v: 1,
        type: raw.type,
        source,
        ...(isCrosstabStart(raw.start) ? { start: raw.start } : {}),
        setup: parseSetupRef(raw.setup),
        standard: parseStandardRef(raw.standard),
    };
}

/**
 * The block for a crosstab saved before X7: today's guess, frozen. No setup and no standard are
 * claimed, because none was recorded when it was made.
 */
export function legacyStudyTypeRecord(type: StudyTypeKey): StudyTypeRecord {
    return { v: 1, type, source: 'suggested', setup: null, standard: null };
}

/**
 * The type a session runs on, from the first source that has one: the record's own block, then the
 * suggestion frozen when the session first saw its questions. Never the live name.
 */
export function resolveStudyType(
    record: StudyTypeRecord | null,
    frozenSuggestion: StudyTypeKey | null,
): StudyTypeKey | null {
    return record?.type ?? frozenSuggestion;
}

/**
 * A copy (Duplicate, or "Copy of a crosstab" at create) keeps the source's type, records that it
 * started as a copy, and keeps the source's standard so its statements stay true; the setup is
 * the source's, not one this copy was built from, so it is dropped.
 */
export function studyTypeForCopy(source: StudyTypeRecord | null): StudyTypeRecord | null {
    if (!source) return null;
    return { ...source, start: 'copy', setup: null };
}
